/* eslint-disable */
/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — SUPABASE SYNC LAYER
   Cloud sync for all platform data
   Falls back to localStorage if Supabase not configured
═══════════════════════════════════════════════════════════ */

import { saveToStorage, loadFromStorage, getSupabaseClient } from "./Auth";

/* ── GET SUPABASE CLIENT ── */
function getSB() {
  return getSupabaseClient();
}

/* ── GET CURRENT USER ID ── */
async function getUserId() {
  const sb = getSB();
  if (!sb) {
    // Fall back to localStorage session
    try {
      const session = loadFromStorage("session");
      return session?.id || null;
    } catch(e) { return null; }
  }
  try {
    const { data: { user } } = await sb.auth.getUser();
    if (user?.id) return user.id;
    // Fall back to localStorage
    const session = loadFromStorage("session");
    return session?.id || null;
  } catch(e) {
    const session = loadFromStorage("session");
    return session?.id || null;
  }
}

function isLocalAccount(userId) {
  return !userId || userId.startsWith("demo_") || userId.startsWith("local_");
}

/* ═══════════════════════════════
   SQUAD SYNC
═══════════════════════════════ */

export async function syncSquadToCloud(squad) {
  saveToStorage("my_squad", squad);
  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return;
  try {
    await sb.from("squad_players").delete().eq("user_id", userId);
    if (squad.length > 0) {
      const rows = squad.map((p, i) => ({
        user_id: userId,
        player_name: p.name,
        position: p.pos,
        style: p.style || "",
        pro_ref: p.proRef || "",
        pace: p.pace || 70,
        shooting: p.shooting || 70,
        passing: p.passing || 70,
        dribbling: p.dribbling || 70,
        defending: p.defending || 50,
        physical: p.physical || 70,
        overall: p.overall || 70,
        age: String(p.age || ""),
        club: p.club || "",
        nationality: p.nat || "",
        squad_num: i + 1,
      }));
      await sb.from("squad_players").insert(rows);
    }
  } catch(e) {
    console.warn("Squad cloud sync failed:", e.message);
  }
}

export async function loadSquadFromCloud() {
  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return loadFromStorage("my_squad", []);
  try {
    const { data, error } = await sb
      .from("squad_players").select("*").eq("user_id", userId).order("squad_num");
    if (error || !data) return loadFromStorage("my_squad", []);
    const squad = data.map((r, i) => ({
      id: r.id,
      name: r.player_name,
      pos: r.position,
      group: posGroup(r.position),
      style: r.style,
      proRef: r.pro_ref,
      pace: r.pace, shooting: r.shooting, passing: r.passing,
      dribbling: r.dribbling, defending: r.defending, physical: r.physical,
      overall: r.overall || Math.round((r.pace+r.shooting+r.passing+r.dribbling+r.defending+r.physical)/6),
      age: r.age, club: r.club, nat: r.nationality, squadNum: r.squad_num || i + 1,
    }));
    saveToStorage("my_squad", squad);
    return squad;
  } catch(e) {
    console.warn("Squad cloud load failed:", e.message);
    return loadFromStorage("my_squad", []);
  }
}

/* ═══════════════════════════════
   SCOUT REPORTS SYNC
═══════════════════════════════ */

export async function saveReportToCloud(report) {
  const sb = getSB();
  const userId = await getUserId();

  if (!sb || !userId || userId.startsWith("demo_") || userId.startsWith("local_")) {
    const reports = loadFromStorage("scout_reports", []);
    const updated = [report, ...reports.filter(r => r.id !== report.id)];
    saveToStorage("scout_reports", updated);
    return report;
  }

  try {
    const row = {
      user_id: userId,
      player_name: report.playerName,
      age: String(report.age || ""),
      position: report.pos,
      club: report.club || "",
      nationality: report.nat || "",
      style: report.style || "",
      match_watched: report.match || "",
      match_date: report.date || null,
      competition: report.competition || "",
      stars: report.stars || 0,
      interest: report.interest || "",
      recommended_fee: report.fee || "",
      pace: report.pace || 70,
      shooting: report.shooting || 70,
      passing: report.passing || 70,
      dribbling: report.dribbling || 70,
      defending: report.defending || 50,
      physical: report.physical || 70,
      notes: report.notes || "",
      strength_notes: report.strengthNotes || "",
      weakness_notes: report.weaknessNotes || "",
    };

    const { data, error } = await sb
      .from("scout_reports")
      .insert([row])
      .select()
      .single();

    if (error) throw error;
    return { ...report, cloudId: data.id };
  } catch(e) {
    console.warn("Report cloud save failed:", e.message);
    // Fallback to localStorage
    const reports = loadFromStorage("scout_reports", []);
    const updated = [report, ...reports];
    saveToStorage("scout_reports", updated);
    return report;
  }
}

export async function loadReportsFromCloud() {
  const sb = getSB();
  const userId = await getUserId();

  if (!sb || !userId || userId.startsWith("demo_") || userId.startsWith("local_")) {
    return loadFromStorage("scout_reports", null);
  }

  try {
    const { data, error } = await sb
      .from("scout_reports")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error || !data) return loadFromStorage("scout_reports", null);

    const reports = data.map(r => ({
      id: r.id,
      cloudId: r.id,
      playerName: r.player_name,
      age: r.age,
      pos: r.position,
      club: r.club,
      nat: r.nationality,
      style: r.style,
      match: r.match_watched,
      date: r.match_date,
      competition: r.competition,
      stars: r.stars,
      interest: r.interest,
      fee: r.recommended_fee,
      pace: r.pace,
      shooting: r.shooting,
      passing: r.passing,
      dribbling: r.dribbling,
      defending: r.defending,
      physical: r.physical,
      notes: r.notes,
      strengthNotes: r.strength_notes,
      weaknessNotes: r.weakness_notes,
      scout: "Me",
      created: new Date(r.created_at).toLocaleDateString("en-GB", {day:"numeric",month:"short",year:"numeric"}),
    }));

    saveToStorage("scout_reports", reports);
    return reports;
  } catch(e) {
    console.warn("Reports cloud load failed:", e.message);
    return loadFromStorage("scout_reports", null);
  }
}

export async function deleteReportFromCloud(reportId) {
  const sb = getSB();
  const userId = await getUserId();

  // Always update locally
  const reports = loadFromStorage("scout_reports", []);
  const updated = reports.filter(r => r.id !== reportId);
  saveToStorage("scout_reports", updated);

  if (!sb || isLocalAccount(userId)) return;

  try {
    await sb.from("scout_reports").delete().eq("id", reportId).eq("user_id", userId);
  } catch(e) {
    console.warn("Report cloud delete failed:", e.message);
  }
}

/* ═══════════════════════════════
   SESSION DRILLS SYNC
═══════════════════════════════ */

export async function addDrillToCloud(drill) {
  // Always save locally first
  const current = loadFromStorage("session_drills", []);
  if (current.find(d => d.title === drill.title)) return false;
  const newDrill = { ...drill, id: Date.now(), addedAt: new Date().toISOString() };
  saveToStorage("session_drills", [...current, newDrill]);

  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return true;

  try {
    await sb.from("session_drills").insert([{
      user_id: userId,
      title: drill.title,
      description: drill.desc || "",
      meta: drill.meta || "",
      source: drill.source || "",
    }]);
  } catch(e) {
    console.warn("Drill cloud save failed:", e.message);
  }
  return true;
}

export async function loadDrillsFromCloud() {
  const sb = getSB();
  const userId = await getUserId();

  if (!sb || !userId || userId.startsWith("demo_") || userId.startsWith("local_")) {
    return loadFromStorage("session_drills", []);
  }

  try {
    const { data, error } = await sb
      .from("session_drills")
      .select("*")
      .eq("user_id", userId)
      .order("added_at", { ascending: false });

    if (error || !data) return loadFromStorage("session_drills", []);

    const drills = data.map(r => ({
      id: r.id,
      title: r.title,
      desc: r.description,
      meta: r.meta,
      source: r.source,
      addedAt: r.added_at,
    }));

    saveToStorage("session_drills", drills);
    return drills;
  } catch(e) {
    console.warn("Drills cloud load failed:", e.message);
    return loadFromStorage("session_drills", []);
  }
}

export async function removeDrillFromCloud(drillId) {
  const updated = loadFromStorage("session_drills", []).filter(d => d.id !== drillId);
  saveToStorage("session_drills", updated);

  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return;

  try {
    await sb.from("session_drills").delete().eq("id", String(drillId)).eq("user_id", userId);
  } catch(e) {
    console.warn("Drill cloud remove failed:", e.message);
  }
}

export async function clearDrillsFromCloud() {
  saveToStorage("session_drills", []);

  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return;

  try {
    await sb.from("session_drills").delete().eq("user_id", userId);
  } catch(e) {
    console.warn("Drills cloud clear failed:", e.message);
  }
}

/* ═══════════════════════════════
   OPPOSITION DOSSIERS SYNC
═══════════════════════════════ */

export async function saveDossierToCloud(dossier) {
  const current = loadFromStorage("dossiers", []);
  const updated = [dossier, ...current.filter(d => d.id !== dossier.id).slice(0, 9)];
  saveToStorage("dossiers", updated);

  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return;

  try {
    await sb.from("opposition_dossiers").insert([{
      user_id: userId,
      opp_name: dossier.oppName,
      opp_style: dossier.oppStyle,
      opp_formation: dossier.oppForm,
      match_date: dossier.matchDate || null,
      competition: dossier.competition || "",
      venue: dossier.venue || "home",
      threats: dossier.threats || [],
    }]);
  } catch(e) {
    console.warn("Dossier cloud save failed:", e.message);
  }
}

export async function loadDossiersFromCloud() {
  const sb = getSB();
  const userId = await getUserId();

  if (!sb || !userId || userId.startsWith("demo_") || userId.startsWith("local_")) {
    return loadFromStorage("dossiers", []);
  }

  try {
    const { data, error } = await sb
      .from("opposition_dossiers")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(10);

    if (error || !data) return loadFromStorage("dossiers", []);

    const dossiers = data.map(r => ({
      id: r.id,
      oppName: r.opp_name,
      oppStyle: r.opp_style,
      oppForm: r.opp_formation,
      matchDate: r.match_date,
      competition: r.competition,
      venue: r.venue,
      threats: r.threats || [],
      generatedAt: new Date(r.created_at).toLocaleDateString("en-GB", {day:"numeric",month:"short",year:"numeric"}),
    }));

    saveToStorage("dossiers", dossiers);
    return dossiers;
  } catch(e) {
    console.warn("Dossiers cloud load failed:", e.message);
    return loadFromStorage("dossiers", []);
  }
}

/* ═══════════════════════════════
   VIDEO ANALYSES SYNC
═══════════════════════════════ */

export async function saveAnalysisToCloud(analysis, matchData) {
  const current = loadFromStorage("va_analyses", []);
  const newEntry = { ...analysis, matchData, date: new Date().toLocaleDateString() };
  saveToStorage("va_analyses", [newEntry, ...current]);

  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return;

  try {
    await sb.from("video_analyses").insert([{
      user_id: userId,
      opponent: matchData.opponent || "",
      match_date: matchData.date || null,
      competition: matchData.competition || "",
      result: matchData.result || "",
      venue: matchData.venue || "home",
      press_efficiency: analysis.pressEfficiency,
      possession_pct: analysis.possessionPct,
      pass_accuracy: analysis.passAccuracy,
      chances_created: analysis.chancesCreated,
      shots_on_target: analysis.shotsOnTarget,
      high_press_wins: analysis.highPressWins,
      distance_covered: parseFloat(analysis.distanceCovered),
      key_moments: analysis.keyMoments || [],
      recommendations: analysis.recommendations || [],
      pressing_zones: analysis.pressingZones || [],
    }]);
  } catch(e) {
    console.warn("Analysis cloud save failed:", e.message);
  }
}

export async function loadAnalysesFromCloud() {
  const sb = getSB();
  const userId = await getUserId();

  if (!sb || !userId || userId.startsWith("demo_") || userId.startsWith("local_")) {
    return loadFromStorage("va_analyses", []);
  }

  try {
    const { data, error } = await sb
      .from("video_analyses")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(20);

    if (error || !data) return loadFromStorage("va_analyses", []);
    return data;
  } catch(e) {
    console.warn("Analyses cloud load failed:", e.message);
    return loadFromStorage("va_analyses", []);
  }
}

/* ═══════════════════════════════
   WATCHLIST SYNC
═══════════════════════════════ */

export async function syncWatchlistToCloud(playerId, playerData, add=true) {
  const sb = getSB();
  const userId = await getUserId();
  if (!sb || isLocalAccount(userId)) return;

  try {
    if (add) {
      await sb.from("watchlist").upsert([{
        user_id: userId,
        player_id: playerId,
        player_name: playerData.name,
        position: playerData.pos,
        club: playerData.club,
        overall: playerData.overall,
      }]);
    } else {
      await sb.from("watchlist").delete()
        .eq("user_id", userId)
        .eq("player_id", playerId);
    }
  } catch(e) {
    console.warn("Watchlist cloud sync failed:", e.message);
  }
}

/* ═══════════════════════════════
   HELPERS
═══════════════════════════════ */

function posGroup(pos) {
  if (pos === "GK") return "gk";
  if (["CB","RB","LB","WB"].includes(pos)) return "def";
  if (["CDM","CM","CAM","RM","LM"].includes(pos)) return "mid";
  return "att";
}

/* ═══════════════════════════════
   SYNC STATUS CHECK
═══════════════════════════════ */

export async function getSyncStatus() {
  const sb = getSB();
  if (!sb) return { syncing: false, reason: "Supabase not configured" };
  try {
    const { data: { user } } = await sb.auth.getUser();
    if (user?.id) return { syncing: true, reason: "Cloud sync active", userId: user.id };
    return { syncing: false, reason: "Not logged in to Supabase" };
  } catch(e) {
    return { syncing: false, reason: "Session check failed" };
  }
}

/* ═══════════════════════════════
   FULL INITIAL SYNC
   Call on login — loads everything from cloud,
   always returns data (falls back to localStorage)
═══════════════════════════════ */

export async function performInitialSync() {
  const status = await getSyncStatus();

  if (!status.syncing) {
    // Not connected — return whatever is in localStorage
    return {
      success: false,
      reason: status.reason,
      squad:    loadFromStorage("my_squad", []),
      reports:  loadFromStorage("scout_reports", null),
      drills:   loadFromStorage("session_drills", []),
      dossiers: loadFromStorage("dossiers", []),
    };
  }

  try {
    const [squad, reports, drills, dossiers] = await Promise.all([
      loadSquadFromCloud(),
      loadReportsFromCloud(),
      loadDrillsFromCloud(),
      loadDossiersFromCloud(),
    ]);

    return {
      success: true,
      squad:    squad    || [],
      reports:  reports  || null,
      drills:   drills   || [],
      dossiers: dossiers || [],
    };
  } catch(e) {
    console.warn("Initial sync failed:", e.message);
    return {
      success: false,
      reason: e.message,
      squad:    loadFromStorage("my_squad", []),
      reports:  loadFromStorage("scout_reports", null),
      drills:   loadFromStorage("session_drills", []),
      dossiers: loadFromStorage("dossiers", []),
    };
  }
}
